import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  Box,
  Typography,
  Checkbox,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Fade,
  CircularProgress,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import _ from "lodash";
import type {
  DataTableHeaderFilterOption,
  DataTableHeaderFilterOptionRenderer,
  SortModel,
} from "../DataTable.types";
import TextField from "../../forms/TextField";
import { MelaButton } from "@/ui/mela";

export interface SortFilterPopoverProps {
  /** Anchor element for the popover */
  anchorEl: HTMLElement | null;
  /** Anchor coordinates for popovers that must survive anchor element remounts */
  anchorPosition?: { top: number; left: number } | null;
  /** Whether the popover is open */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Column sort key */
  sortKey: string;
  /** Column header label */
  headerLabel: string;
  /** Popover type: multi-select (default), search, or custom content */
  type?: "select" | "search" | "custom";
  /** Custom content for custom popovers */
  renderCustomContent?: (props: { onClose: () => void }) => React.ReactNode;
  /** Current search value (for search type) */
  searchValue?: string;
  /** Placeholder for search input */
  searchPlaceholder?: string;
  /** Callback when search value changes (for search type) */
  onSearchChange?: (value: string) => void;
  /** Current sort model */
  sortModel?: SortModel | null;
  /** Callback when sort changes */
  onSortChange: (model: SortModel | null) => void;
  /** Filter options list */
  filterOptions: DataTableHeaderFilterOption[];
  /** Optional renderer for each filter option row */
  renderFilterOption?: DataTableHeaderFilterOptionRenderer;
  /** Currently selected filter values */
  selectedValues: string[];
  /** Whether to hide sort buttons in the header filter popover */
  hideSort?: boolean;
  /** Whether to show a search input in the header filter popover to search options locally */
  showSearch?: boolean;
  /** How select filter search is handled. Defaults to local option filtering. */
  searchMode?: "local" | "remote";
  /** Whether filter options are loading */
  loading?: boolean;
  /** Whether to show the select-all row for multiple select filters. Defaults to true. */
  showSelectAll?: boolean;
  /** Whether select filters allow multiple values. Defaults to true. */
  multiple?: boolean;
  /** Callback when Apply is clicked with selected filter values and sort */
  onApply: (selected: string[], sort: SortModel | null) => void;
}

const POPOVER_MAX_HEIGHT = 400;

export const SortFilterPopover: React.FC<SortFilterPopoverProps> = ({
  anchorEl,
  anchorPosition,
  open,
  onClose,
  sortKey,
  headerLabel,
  sortModel,
  onSortChange,
  type = "select",
  renderCustomContent,
  searchValue = "",
  searchPlaceholder = "Search...",
  onSearchChange,
  filterOptions,
  renderFilterOption,
  selectedValues,
  onApply,
  hideSort = false,
  showSearch = false,
  searchMode = "local",
  loading = false,
  showSelectAll = true,
  multiple = true,
}) => {
  const { t } = useTranslation();
  // Local draft state — committed only on Apply for select type
  // For search type, search is live but sort is committed on Apply
  const [draftSelected, setDraftSelected] = useState<string[]>(selectedValues);
  const [draftSort, setDraftSort] = useState<SortModel | null>(
    sortModel?.field === sortKey ? sortModel : null,
  );
  const [draftSearch, setDraftSearch] = useState<string>(searchValue);
  const [localSearch, setLocalSearch] = useState<string>("");

  // Filter options based on local search if showSearch is true
  const filteredOptions = useMemo(() => {
    if (!showSearch || searchMode === "remote" || !localSearch) {
      return filterOptions;
    }
    const lowerSearch = localSearch.toLowerCase();
    return filterOptions.filter((opt) =>
      opt.label.toLowerCase().includes(lowerSearch),
    );
  }, [filterOptions, localSearch, searchMode, showSearch]);

  // Sync draft state when popover opens or searchValue changes from outside
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setDraftSelected(selectedValues);
      setDraftSort(sortModel?.field === sortKey ? sortModel : null);
    }
    wasOpenRef.current = open;
  }, [open, selectedValues, sortModel, sortKey]);

  useEffect(() => {
    setDraftSearch(searchValue);
  }, [searchValue]);

  // Debounced search change for live search
  const debouncedSearchChange = useMemo(
    () =>
      _.debounce((value: string) => {
        if (onSearchChange) {
          onSearchChange(value);
        }
      }, 300),
    [onSearchChange],
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearchChange.flush();
    };
  }, [debouncedSearchChange]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setDraftSearch(value);
      debouncedSearchChange(value);
    },
    [debouncedSearchChange],
  );

  const currentDirection =
    draftSort?.field === sortKey ? draftSort.direction : null;

  const handleSortClick = useCallback(
    (direction: "asc" | "desc") => {
      const next = { field: sortKey, direction } as SortModel;
      setDraftSort(next);
      onSortChange(next);
    },
    [sortKey, onSortChange],
  );

  const handleToggleOption = useCallback(
    (value: string) => {
      setDraftSelected((prev) => {
        const isSelected = prev.includes(value);
        if (!multiple) {
          return isSelected ? [] : [value];
        }

        return isSelected ? prev.filter((v) => v !== value) : [...prev, value];
      });
    },
    [multiple],
  );

  const handleSelectAll = useCallback(() => {
    setDraftSelected((prev) => {
      const currentOptions = showSearch ? filteredOptions : filterOptions;
      const allCurrentSelected = currentOptions.every((o) =>
        prev.includes(o.value),
      );

      if (allCurrentSelected) {
        const currentValues = currentOptions.map((o) => o.value);
        return prev.filter((v) => !currentValues.includes(v));
      } else {
        const newSelected = [...prev];
        currentOptions.forEach((o) => {
          if (!newSelected.includes(o.value)) {
            newSelected.push(o.value);
          }
        });
        return newSelected;
      }
    });
  }, [filterOptions, filteredOptions, showSearch]);

  const handleApply = useCallback(() => {
    onApply(draftSelected, draftSort);
    onClose();
  }, [draftSelected, draftSort, onApply, onClose]);

  const hasDraftSelection = draftSelected.length > 0;
  const handleClear = useCallback(() => {
    if (!hasDraftSelection) return;

    onApply([], draftSort);
    onClose();
  }, [draftSort, hasDraftSelection, onApply, onClose]);

  const currentOptions = showSearch ? filteredOptions : filterOptions;
  const selectSearchValue = searchMode === "remote" ? draftSearch : localSearch;
  const handleSelectSearchChange = (value: string) => {
    if (searchMode === "remote") {
      handleSearchChange(value);
      return;
    }

    setLocalSearch(value);
  };

  const allSelected =
    multiple &&
    currentOptions.length > 0 &&
    currentOptions.every((o) => draftSelected.includes(o.value));
  const someSelected =
    multiple &&
    !allSelected &&
    currentOptions.some((o) => draftSelected.includes(o.value));
  const isCustom = type === "custom";

  return (
    <Popover
      open={open}
      anchorEl={anchorPosition ? undefined : anchorEl}
      anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
      anchorPosition={anchorPosition || undefined}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 200 }}
      slotProps={{
        paper: {
          sx: {
            mt: 0.5,
            borderRadius: "8px",
            border: "1px solid var(--mela-border-subtle)",
            boxShadow: "var(--mela-shadow-lg)",
            minWidth: isCustom ? "auto" : 240,
            maxWidth: isCustom ? "none" : 320,
            maxHeight: isCustom ? "none" : POPOVER_MAX_HEIGHT,
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pt: 2,
          pb: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ color: "text.primary", fontWeight: 600, fontSize: "0.9375rem" }}
        >
          {headerLabel}
        </Typography>

        <MelaButton
          iconOnly
          size="sm"
          variant="ghost"
          tone="neutral"
          onClick={onClose}
          aria-label="close"
          startIcon={<CloseIcon fontSize="small" />}
          style={{
            minWidth: 24,
            width: 24,
            height: 24,
            padding: 0,
            borderRadius: "50%",
            boxShadow: "none",
            color: "var(--mui-palette-text-secondary)",
          }}
        />
      </Box>

      {/* Sort direction buttons */}
      {!hideSort && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            px: 2,
            pb: 1,
            flexShrink: 0,
          }}
        >
          <Box
            onClick={() => handleSortClick("asc")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: "6px",
              cursor: "pointer",
              border: "1px solid",
              borderColor:
                currentDirection === "asc"
                  ? "var(--mela-color-primary)"
                  : "var(--mela-border-default)",
              backgroundColor:
                currentDirection === "asc"
                  ? "var(--mela-color-primary-soft-bg)"
                  : "transparent",
              "&:hover": {
                backgroundColor: "var(--mela-color-primary-soft-hover-bg)",
              },
              transition: "all 0.15s",
            }}
          >
            <ArrowUpwardIcon
              sx={{
                fontSize: 16,
                color:
                  currentDirection === "asc"
                    ? "var(--mela-color-primary)"
                    : "var(--mela-text-muted)",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color:
                  currentDirection === "asc"
                    ? "var(--mela-color-primary)"
                    : "var(--mela-text-secondary)",
                fontWeight: currentDirection === "asc" ? 600 : 400,
              }}
            >
              A→Z
            </Typography>
          </Box>
          <Box
            onClick={() => handleSortClick("desc")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: "6px",
              cursor: "pointer",
              border: "1px solid",
              borderColor:
                currentDirection === "desc"
                  ? "var(--mela-color-primary)"
                  : "var(--mela-border-default)",
              backgroundColor:
                currentDirection === "desc"
                  ? "var(--mela-color-primary-soft-bg)"
                  : "transparent",
              "&:hover": {
                backgroundColor: "var(--mela-color-primary-soft-hover-bg)",
              },
              transition: "all 0.15s",
            }}
          >
            <ArrowDownwardIcon
              sx={{
                fontSize: 16,
                color:
                  currentDirection === "desc"
                    ? "var(--mela-color-primary)"
                    : "var(--mela-text-muted)",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color:
                  currentDirection === "desc"
                    ? "var(--mela-color-primary)"
                    : "var(--mela-text-secondary)",
                fontWeight: currentDirection === "desc" ? 600 : 400,
              }}
            >
              Z→A
            </Typography>
          </Box>
        </Box>
      )}

      <Divider />

      {/* Content */}
      {type === "custom" ? (
        renderCustomContent?.({ onClose })
      ) : type === "search" ? (
        <Box sx={{ px: 2, py: 1, flexShrink: 0 }}>
          <TextField
            type="search"
            AccessoryStart={<SearchIcon />}
            placeholder={searchPlaceholder}
            value={draftSearch}
            autoFocus
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
              },
            }}
          />
        </Box>
      ) : (
        <>
          {showSearch && (
            <Box sx={{ px: 2, py: 1, flexShrink: 0 }}>
              <TextField
                type="search"
                AccessoryStart={<SearchIcon />}
                placeholder={
                  searchPlaceholder ||
                  t("finance.common.searchPlaceholder.translation")
                }
                value={selectSearchValue}
                onChange={(e) => handleSelectSearchChange(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    height: 36,
                  },
                  "& .MuiOutlinedInput-input": {
                    py: 0.75,
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputAdornment-root svg": {
                    fontSize: 20,
                  },
                }}
              />
            </Box>
          )}
          <List
            dense
            sx={{
              overflowY: "auto",
              py: 0.5,
              flex: 1,
            }}
          >
            {/* Select all / Deselect all */}
            {currentOptions.length > 0 ? (
              <>
                {multiple && showSelectAll && (
                  <ListItemButton onClick={handleSelectAll} sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        checked={allSelected}
                        indeterminate={someSelected}
                        size="small"
                        sx={{ padding: "4px" }}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        allSelected
                          ? t("tasks.deselectAll.translation")
                          : t("profile.templates.selectAll.translation")
                      }
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: allSelected ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                )}

                {currentOptions.map((option) => {
                  const isChecked = draftSelected.includes(option.value);
                  const optionContent = renderFilterOption
                    ? renderFilterOption(option, { checked: isChecked })
                    : option.label;

                  return (
                    <ListItemButton
                      key={option.value}
                      onClick={() => handleToggleOption(option.value)}
                      sx={{ py: 0.25 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={isChecked}
                          size="small"
                          sx={{ padding: "4px" }}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={optionContent}
                        disableTypography={!!renderFilterOption}
                        primaryTypographyProps={
                          renderFilterOption
                            ? undefined
                            : {
                                variant: "body2",
                                fontWeight: isChecked ? 500 : 400,
                              }
                        }
                      />
                    </ListItemButton>
                  );
                })}
              </>
            ) : loading ? (
              <Box sx={{ px: 2, py: 2, textAlign: "center" }}>
                <CircularProgress size={18} />
              </Box>
            ) : (
              <Box sx={{ px: 2, py: 2, textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("tasks.nothingFound.translation")}
                </Typography>
              </Box>
            )}
          </List>
        </>
      )}

      {/* Apply button */}
      {type === "select" && (
        <>
          <Divider />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              px: 2,
              py: 1,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="body2"
              onClick={handleClear}
              sx={{
                color: hasDraftSelection ? "primary.main" : "text.disabled",
                cursor: hasDraftSelection ? "pointer" : "default",
                userSelect: "none",
              }}
            >
              {t("imageEditor.action.clear.translation")}
            </Typography>

            <MelaButton
              variant="solid"
              tone="primary"
              size="sm"
              onClick={handleApply}
              style={{
                borderRadius: "6px",
                color: "var(--mela-color-primary-solid-text)",
                backgroundColor: "var(--mela-color-primary-solid-bg)",
              }}
            >
              {t("ui.dataRange.actions.apply")}
            </MelaButton>
          </Box>
        </>
      )}
    </Popover>
  );
};
